import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    type NCoreUIKitIcon,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NumericInput,
    RadioButton,
    Breadcrumb,
    RadioGroup,
    Accordion,
    Separator,
    Timeline,
    Avatar,
    Button,
    Alert,
    Badge,
    Card,
    Tabs,
    Item,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    CalendarDays,
    ChevronRight,
    Navigation,
    MapPin,
    Search,
    Ticket,
    Clock,
    Music,
    Info
} from "lucide-react-native";
import Svg, {
    Ellipse,
    Circle,
    Path,
    Rect,
    Line
} from "react-native-svg";
import {
    createKitIcon
} from "../components/kitIcons";

const CalendarDaysIcon = createKitIcon(CalendarDays);

const ClockIcon = createKitIcon(Clock);

const MapPinIcon = createKitIcon(MapPin);

const TicketIcon = createKitIcon(Ticket);

const SearchIcon = createKitIcon(Search);

const MusicIcon = createKitIcon(Music);

const NavigationIcon = createKitIcon(Navigation);

const ChevronRightIcon = createKitIcon(ChevronRight);

const InfoIcon = createKitIcon(Info);

type StageKey = "main" | "club" | "terrace";

type ProgramItem = {
    stage: StageKey;
    time: string;
    key: string;
};

const STAGE_COLORS: Record<StageKey, "2" | "3" | "4"> = {
    terrace: "4",
    main: "2",
    club: "3"
};

const NAV = [
    "events",
    "festivals",
    "venues",
    "tickets"
] as const;

const DAYS: Array<{
    items: Array<ProgramItem>;
    key: string;
}> = [
    {
        items: [
            {
                time: "18:00 – 19:00",
                key: "orchestra",
                stage: "main"
            },
            {
                time: "19:15 – 20:15",
                stage: "terrace",
                key: "trio"
            },
            {
                time: "20:30 – 22:00",
                key: "quartet",
                stage: "main"
            },
            {
                time: "22:15 – 00:30",
                key: "night",
                stage: "club"
            }
        ],
        key: "fri"
    },
    {
        items: [
            {
                time: "16:00 – 17:00",
                stage: "terrace",
                key: "kids"
            },
            {
                time: "18:00 – 19:15",
                key: "quintet",
                stage: "main"
            },
            {
                time: "19:30 – 20:30",
                key: "anatolia",
                stage: "main"
            },
            {
                time: "21:00 – 22:30",
                stage: "club",
                key: "sax"
            },
            {
                time: "23:00 – 00:30",
                stage: "club",
                key: "jam"
            }
        ],
        key: "sat"
    },
    {
        items: [
            {
                time: "16:00 – 17:00",
                stage: "terrace",
                key: "brunch"
            },
            {
                time: "17:30 – 18:45",
                key: "young",
                stage: "club"
            },
            {
                time: "19:30 – 22:00",
                key: "finale",
                stage: "main"
            }
        ],
        key: "sun"
    }
];

const ARTISTS = [
    "elif",
    "deniz",
    "marta",
    "zeynep",
    "can"
] as const;

const TICKETS = [
    {
        price: 650,
        key: "day"
    },
    {
        key: "festival",
        price: 1900
    },
    {
        key: "student",
        price: 390
    }
] as const;

const FAQ = [
    "kids",
    "refund",
    "food",
    "rain"
] as const;

const VENUE_INFO = [
    "ferry",
    "parking",
    "access"
] as const;

const MAP_LABELS = [
    {
        key: "sea",
        x: 6,
        y: 16
    },
    {
        key: "street",
        x: 26,
        y: 30
    },
    {
        key: "ferry",
        x: 12,
        y: 40
    },
    {
        key: "walk",
        x: 36,
        y: 52
    },
    {
        key: "avenue",
        x: 60,
        y: 36
    },
    {
        key: "road",
        x: 76,
        y: 62
    },
    {
        key: "park",
        x: 54,
        y: 76
    }
] as const;

const SERVICE_FEE = 95;

const MAX_TICKETS = 8;

const POSTER_RATIO = 1280 / 440;

const MAP_RATIO = 840 / 340;

const WHITE_KEYS = Array.from({
    length: 16
}, (_, index) => index * 40 + 1);

const BLACK_KEYS = Array.from({
    length: 15
}, (_, index) => index).filter((index) => [
    0,
    1,
    3,
    4,
    5
].includes(index % 7)).map((index) => (index + 1) * 40 - 12);

const STAFF_LINES = [
    190,
    208,
    226,
    244,
    262
];

const MAP_STREETS = [
    "M190 0 L330 130 L560 270 L600 340",
    "M470 0 L500 140 L520 205 L560 340",
    "M0 250 L420 230 L840 250",
    "M640 0 L700 200 L720 340"
];

const FOOTER_LINKS = [
    "sales",
    "kvkk",
    "contact"
];

const stylesheet = StyleSheet.create({
    header: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        width: "100%"
    },
    spread: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    overlay: {
        ...StyleSheet.absoluteFill
    },
    footerCompact: {
        alignItems: "flex-start",
        flexDirection: "column"
    },
    page: {
        alignItems: "center",
        minHeight: "100%",
        flexGrow: 1
    },
    row: {
        alignItems: "center",
        flexDirection: "row"
    },
    wrap: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    absolute: {
        position: "absolute"
    },
    venueCompact: {
        alignSelf: "stretch"
    },
    clip: {
        overflow: "hidden",
        width: "100%"
    },
    shrink: {
        flexShrink: 1,
        minWidth: 0
    },
    shell: {
        width: "100%"
    },
    cell: {
        flexGrow: 1,
        minWidth: 0
    },
    grow: {
        flex: 1,
        flexGrow: 1
    },
    line: {
        flex: 1
    }
});

const createStageDot = (stage: StageKey) => {
    const StageDot: NCoreUIKitIcon = ({
        size
    }) => {
        const {
            radiuses,
            colors
        } = NCoreUIKitTheme.useContext();

        const dotSize = typeof size === "number" ? size / 2 : 8;

        return <View
            style={{
                backgroundColor: colors.chart.categorical[STAGE_COLORS[stage]],
                borderRadius: radiuses.full,
                height: dotSize,
                width: dotSize
            }}
        />;
    };

    return StageDot;
};

const STAGE_ICONS: Record<StageKey, NCoreUIKitIcon> = {
    terrace: createStageDot("terrace"),
    main: createStageDot("main"),
    club: createStageDot("club")
};

const STAGES: Array<StageKey> = [
    "main",
    "club",
    "terrace"
];

const Poster = () => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        categorical
    } = colors.chart;

    const {
        container,
        text
    } = colors.content;

    return <Svg
        preserveAspectRatio="xMaxYMid slice"
        viewBox="0 0 1280 440"
        height="100%"
        width="100%"
    >
        <Rect
            fill={container.inverse}
            height={440}
            width={1280}
            x={0}
            y={0}
        />
        <Circle
            stroke={text.contrastLow}
            strokeOpacity={0.35}
            strokeWidth={1}
            fill="none"
            cx={880}
            cy={200}
            r={300}
        />
        <Circle
            stroke={text.contrastLow}
            strokeOpacity={0.35}
            strokeWidth={1}
            fill="none"
            cx={880}
            cy={200}
            r={360}
        />
        <Circle
            fill={categorical["2"]}
            cx={975}
            cy={190}
            r={200}
        />
        {
            STAFF_LINES.map((y) => <Line
                stroke={container.inverse}
                strokeOpacity={0.35}
                strokeWidth={6}
                x2={1160}
                x1={800}
                key={y}
                y1={y}
                y2={y}
            />)
        }
        <Circle
            fill={categorical["4"]}
            cx={1131}
            cy={150}
            r={112}
        />
        <Circle
            fill={categorical["1"]}
            cx={760}
            cy={96}
            r={44}
        />
        <Circle
            fill={categorical["3"]}
            cx={890}
            cy={438}
            r={155}
        />
        <Circle
            stroke={categorical["8"]}
            strokeWidth={36}
            fill="none"
            cx={1196}
            cy={345}
            r={48}
        />
        <Ellipse
            fill={text.contrastHigh}
            cx={560}
            cy={301}
            rx={32}
            ry={24}
        />
        <Path
            stroke={text.contrastHigh}
            strokeLinecap="round"
            d="M587 296 V147"
            strokeWidth={9}
            fill="none"
        />
        <Path
            d="M587 147 Q650 150 680 215"
            stroke={text.contrastHigh}
            strokeLinecap="round"
            strokeWidth={9}
            fill="none"
        />
        {
            WHITE_KEYS.map((x) => <Rect
                fill={container.default}
                height={88}
                width={38}
                key={x}
                y={352}
                x={x}
            />)
        }
        {
            BLACK_KEYS.map((x) => <Rect
                fill={container.inverse}
                height={52}
                width={24}
                key={x}
                y={352}
                x={x}
            />)
        }
    </Svg>;
};

const VenueMap = () => {
    const {
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        categoricalArea,
        categorical
    } = colors.chart;

    const {
        container
    } = colors.content;

    return <Svg
        preserveAspectRatio="xMidYMid slice"
        viewBox="0 0 840 340"
        height="100%"
        width="100%"
    >
        <Rect
            fill={container.subtle}
            height={340}
            width={840}
            x={0}
            y={0}
        />
        <Path
            d="M0 0 H200 Q130 120 100 200 Q60 280 0 300 Z"
            fill={categoricalArea["3"]}
        />
        <Rect
            fill={categoricalArea["3"]}
            height={50}
            width={840}
            y={290}
            x={0}
        />
        {
            MAP_STREETS.map((value) => <Path
                stroke={container.default}
                strokeLinecap="round"
                strokeWidth={16}
                fill="none"
                key={value}
                d={value}
            />)
        }
        <Ellipse
            fill={categoricalArea["1"]}
            cx={546}
            cy={248}
            rx={107}
            ry={60}
        />
        <Path
            d="M150 92 L330 150 L430 192 L520 205"
            stroke={categorical["3"]}
            strokeLinejoin="round"
            strokeLinecap="round"
            strokeWidth={5}
            fill="none"
        />
        <Circle
            fill={categorical["3"]}
            cx={150}
            cy={92}
            r={14}
        />
        <Circle
            fill={container.default}
            cx={150}
            cy={92}
            r={5}
        />
        <Path
            d="M520 205 L506 168 A34 34 0 1 1 534 168 Z"
            fill={categorical["2"]}
        />
        <Circle
            fill={container.default}
            cx={520}
            cy={150}
            r={9}
        />
    </Svg>;
};

const handlePress = () => undefined;

const Events01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        activeTheme,
        radiuses,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        nav,
        setNav
    ] = useState<string>("festivals");

    const [
        tab,
        setTab
    ] = useState("program");

    const [
        ticket,
        setTicket
    ] = useState("festival");

    const [
        quantity,
        setQuantity
    ] = useState("2");

    const [
        isOrdered,
        setIsOrdered
    ] = useState(false);

    const isWide = width >= shapeConfigs.wideBreakpoint;

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const currency = new Intl.NumberFormat(activeLocale, {
        currencyDisplay: "narrowSymbol",
        style: "currency",
        currency: "TRY"
    });

    const wholeCurrency = new Intl.NumberFormat(activeLocale, {
        currencyDisplay: "narrowSymbol",
        maximumFractionDigits: 0,
        style: "currency",
        currency: "TRY"
    });

    const count = Math.min(MAX_TICKETS, Math.max(1, Math.floor(Number(quantity) || 1)));

    const selected = TICKETS.find((item) => item.key === ticket) ?? TICKETS[1];

    const subtotal = selected.price * count;

    const total = subtotal + SERVICE_FEE;

    const posterRatio = isWide ? POSTER_RATIO : isCompact ? 1.2 : 2;

    const handleNavPress = (key: string) => () => setNav(key);

    const handleTabChange = (value: string) => setTab(value);

    const handleTicketChange = (value: string) => {
        setTicket(value);

        setIsOrdered(false);
    };

    const handleQuantityChange = (value: string) => {
        setQuantity(value);

        setIsOrdered(false);
    };

    const handleOrderPress = () => setIsOrdered(true);

    const renderNav = (key: string) => <Button
        variant={nav === key ? "tonal" : "ghost"}
        title={t(`blocks.events01.nav.${key}`)}
        onPress={handleNavPress(key)}
        type="neutral"
        size="small"
        key={key}
    />;

    const renderFooterLink = (key: string) => <Button
        title={t(`blocks.events01.${key}`)}
        onPress={handlePress}
        variant="link"
        type="neutral"
        size="small"
        key={key}
    />;

    const renderStage = (stage: StageKey) => <Badge
        title={t(`blocks.events01.stage.${stage}`)}
        icon={STAGE_ICONS[stage]}
        variant="outline"
        type="neutral"
        key={stage}
    />;

    const renderProgramItem = (item: ProgramItem) => <Timeline.Item
        description={`${item.time} · ${t(`blocks.events01.program.${item.key}.note`)}`}
        title={t(`blocks.events01.program.${item.key}.title`)}
        icon={MusicIcon}
        key={item.key}
    >
        <View
            style={stylesheet.wrap}
        >
            <Badge
                title={t(`blocks.events01.stage.${item.stage}`)}
                icon={STAGE_ICONS[item.stage]}
                variant="outline"
                type="neutral"
            />
        </View>
    </Timeline.Item>;

    const renderDay = ({
        items,
        key
    }: typeof DAYS[number]) => <View
        style={{
            gap: spaces.spacingMd
        }}
        key={key}
    >
        <View
            style={[
                stylesheet.row,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Text
                accessibilityRole="header"
                variant="titleMediumSize"
                color="high"
            >
                {t(`blocks.events01.day.${key}`)}
            </Text>
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.events01.concerts", [
                    items.length
                ])}
            </Text>
            <View
                style={stylesheet.line}
            >
                <Separator/>
            </View>
        </View>
        <Timeline
            accessibilityLabel={t(`blocks.events01.day.${key}`)}
            timeBehaviour="absolute"
        >
            {items.map(renderProgramItem)}
        </Timeline>
    </View>;

    const renderArtist = (key: string) => <View
        style={[
            stylesheet.cell,
            {
                flexBasis: shapeConfigs.fieldMinWidth * 0.6,
                gap: spaces.spacingSm
            }
        ]}
        key={key}
    >
        <Avatar
            title={t(`blocks.events01.artist.${key}`)}
            size="large"
        />
        <View>
            <Text
                variant="titleSmallSize"
                color="high"
            >
                {t(`blocks.events01.artist.${key}`)}
            </Text>
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t(`blocks.events01.artist.${key}Role`)}
            </Text>
        </View>
    </View>;

    const renderVenueInfo = (key: string) => <View
        style={[
            stylesheet.cell,
            {
                flexBasis: shapeConfigs.fieldMinWidth,
                padding: spaces.spacingMd,
                gap: spaces.spacingXxs
            }
        ]}
        key={key}
    >
        <Text
            variant="labelMediumSize"
            color="high"
        >
            {t(`blocks.events01.venueInfo.${key}`)}
        </Text>
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {t(`blocks.events01.venueInfo.${key}Body`)}
        </Text>
    </View>;

    const renderMapLabel = ({
        key,
        x,
        y
    }: typeof MAP_LABELS[number]) => <Text
        style={[
            stylesheet.absolute,
            {
                left: `${x}%`,
                top: `${y}%`
            }
        ]}
        variant="bodySmallSize"
        pointerEvents="none"
        color="mid"
        key={key}
    >
        {t(`blocks.events01.map.${key}`)}
    </Text>;

    const renderTicket = ({
        price,
        key
    }: typeof TICKETS[number]) => <RadioButton
        title={`${t(`blocks.events01.ticket.${key}`)} · ${wholeCurrency.format(price)}`}
        hintText={key === "festival" ? t("blocks.events01.ticketBest") : undefined}
        subTitle={t(`blocks.events01.ticket.${key}Body`)}
        spreadBehaviour="stretch"
        variant="card"
        value={key}
        key={key}
    />;

    const renderFaq = (key: string) => <Accordion.Item
        value={key}
        key={key}
    >
        <Accordion.Trigger
            title={t(`blocks.events01.faq.${key}`)}
            headingLevel={3}
        />
        <Accordion.Content>
            <Text
                style={{
                    maxWidth: shapeConfigs.readingMaxWidth + shapeConfigs.fieldMinWidth
                }}
                variant="bodyMediumSize"
                color="mid"
            >
                {t(`blocks.events01.faq.${key}Body`)}
            </Text>
        </Accordion.Content>
    </Accordion.Item>;

    const programContent = <View
        style={{
            gap: spaces.spacingLg
        }}
    >
        <View
            style={[
                stylesheet.spread,
                stylesheet.wrap,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.shrink,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    variant="headlineSmallSize"
                    accessibilityRole="header"
                    color="high"
                >
                    {t("blocks.events01.programTitle")}
                </Text>
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t("blocks.events01.programLead")}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.wrap,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                {STAGES.map(renderStage)}
            </View>
        </View>
        {DAYS.map(renderDay)}
    </View>;

    const artistsContent = <View
        style={{
            gap: spaces.spacingMd
        }}
    >
        <View
            style={stylesheet.spread}
        >
            <Text
                variant="headlineSmallSize"
                accessibilityRole="header"
                color="high"
            >
                {t("blocks.events01.artistsTitle")}
            </Text>
            <Button
                title={t("blocks.events01.seeAll")}
                icon={ChevronRightIcon}
                iconDirection="right"
                onPress={handlePress}
                variant="ghost"
                type="neutral"
                size="small"
            />
        </View>
        <View
            style={[
                stylesheet.wrap,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            {ARTISTS.map(renderArtist)}
        </View>
    </View>;

    const venueCard = <Card
        style={isCompact ? stylesheet.venueCompact : [
            stylesheet.absolute,
            {
                width: shapeConfigs.cardWidth * 0.85,
                bottom: spaces.spacingMd,
                left: spaces.spacingMd
            }
        ]}
        variant="outline"
    >
        <Card.Content>
            <Text
                variant="titleSmallSize"
                color="high"
            >
                {t("blocks.events01.venueName")}
            </Text>
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.events01.venueAddress")}
            </Text>
            <View
                style={stylesheet.row}
            >
                <Button
                    title={t("blocks.events01.directions")}
                    icon={NavigationIcon}
                    onPress={handlePress}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </View>
        </Card.Content>
    </Card>;

    const venueContent = <View
        style={{
            gap: spaces.spacingMd
        }}
    >
        <Text
            variant="headlineSmallSize"
            accessibilityRole="header"
            color="high"
        >
            {t("blocks.events01.venueTitle")}
        </Text>
        <Card
            variant="outline"
        >
            <View
                style={[
                    stylesheet.clip,
                    {
                        aspectRatio: MAP_RATIO
                    }
                ]}
            >
                <VenueMap/>
                <View
                    style={stylesheet.overlay}
                    pointerEvents="none"
                >
                    {MAP_LABELS.map(renderMapLabel)}
                </View>
                {isCompact ? null : venueCard}
            </View>
            {isCompact ? venueCard : null}
            <Separator/>
            <View
                style={stylesheet.wrap}
            >
                {VENUE_INFO.map(renderVenueInfo)}
            </View>
        </Card>
    </View>;

    return <ScrollView
        contentContainerStyle={stylesheet.page}
    >
        <View
            style={[
                stylesheet.header,
                {
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingSm,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <Text
                    accessibilityRole="header"
                    variant="titleLargeSize"
                    color="high"
                >
                    {t("blocks.events01.brand")}
                </Text>
                {
                    isCompact ?
                        null
                    :
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            {NAV.map(renderNav)}
                        </View>
                }
            </View>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Button
                    accessibilityLabel={t("blocks.events01.search")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    icon={SearchIcon}
                    variant="ghost"
                    type="neutral"
                    size="small"
                />
                <Button
                    title={t("blocks.events01.signIn")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </View>
        </View>
        <Separator/>
        <View
            style={[
                stylesheet.shell,
                {
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingXl
                }
            ]}
        >
            <Breadcrumb
                accessibilityLabel={t("blocks.events01.breadcrumb")}
                compactBehaviour="collapse"
            >
                <Breadcrumb.List>
                    <Breadcrumb.Item>
                        <Breadcrumb.Link
                            title={t("blocks.events01.nav.festivals")}
                            onPress={handlePress}
                        />
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <Breadcrumb.Page
                            title={t("blocks.events01.title")}
                        />
                    </Breadcrumb.Item>
                </Breadcrumb.List>
            </Breadcrumb>
            <View
                style={[
                    stylesheet.clip,
                    {
                        borderRadius: radiuses.surface,
                        aspectRatio: posterRatio
                    }
                ]}
            >
                <Poster/>
                <View
                    style={[
                        stylesheet.absolute,
                        {
                            left: isCompact ? spaces.spacingMd : spaces.spacingXxl,
                            top: isCompact ? spaces.spacingLg : spaces.spacingXxl,
                            alignItems: "flex-start",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Badge
                        customTheme={{
                            themeKey: activeTheme === "dark" ? "light" : "dark"
                        }}
                        title={t("blocks.events01.posterFacts")}
                        type="neutral"
                    />
                    <Text
                        variant={isCompact ? "displaySmallSize" : "displayMediumSize"}
                        accessibilityRole="header"
                        color="contrastHigh"
                    >
                        {t("blocks.events01.posterTitle")}
                    </Text>
                </View>
            </View>
            <Card
                variant="outline"
            >
                <Card.Content>
                    <View
                        style={[
                            stylesheet.wrap,
                            {
                                alignItems: "center",
                                gap: spaces.spacingLg
                            }
                        ]}
                    >
                        {
                            [
                                {
                                    icon: CalendarDaysIcon,
                                    key: "date"
                                },
                                {
                                    icon: ClockIcon,
                                    key: "time"
                                },
                                {
                                    icon: MapPinIcon,
                                    key: "place"
                                }
                            ].map(({
                                icon,
                                key
                            }) => <View
                                style={[
                                    stylesheet.cell,
                                    {
                                        flexBasis: shapeConfigs.fieldMinWidth + spaces.spacingXl
                                    }
                                ]}
                                key={key}
                            >
                                <Item
                                    size="xSmall"
                                >
                                    <Item.Media
                                        variant="plain"
                                        icon={icon}
                                    />
                                    <Item.Content>
                                        <Item.Description>
                                            {t(`blocks.events01.info.${key}`)}
                                        </Item.Description>
                                        <Item.Title>
                                            {t(`blocks.events01.info.${key}Value`)}
                                        </Item.Title>
                                        <Item.Description>
                                            {t(`blocks.events01.info.${key}Note`)}
                                        </Item.Description>
                                    </Item.Content>
                                </Item>
                            </View>)
                        }
                        <Button
                            spreadBehaviour={isWide ? "free" : "stretch"}
                            title={t("blocks.events01.buy")}
                            onPress={handlePress}
                            icon={TicketIcon}
                            variant="filled"
                            type="primary"
                            size="large"
                        />
                    </View>
                </Card.Content>
            </Card>
            <View
                style={{
                    flexDirection: isWide ? "row" : "column",
                    alignItems: "flex-start",
                    gap: spaces.spacingXl
                }}
            >
                <View
                    style={[
                        stylesheet.shrink,
                        {
                            width: isWide ? undefined : "100%",
                            flex: isWide ? 1 : undefined,
                            gap: spaces.spacingXl
                        }
                    ]}
                >
                    <Tabs
                        onValueChange={handleTabChange}
                        variant="line"
                        value={tab}
                    >
                        <Tabs.List
                            title={t("blocks.events01.tabsTitle")}
                        >
                            <Tabs.Trigger
                                title={t("blocks.events01.tabProgram")}
                                value="program"
                            />
                            <Tabs.Trigger
                                title={t("blocks.events01.tabArtists")}
                                value="artists"
                            />
                            <Tabs.Trigger
                                title={t("blocks.events01.tabVenue")}
                                value="venue"
                            />
                        </Tabs.List>
                        <Tabs.Content
                            value="program"
                        >
                            {programContent}
                        </Tabs.Content>
                        <Tabs.Content
                            value="artists"
                        >
                            {artistsContent}
                        </Tabs.Content>
                        <Tabs.Content
                            value="venue"
                        >
                            {venueContent}
                        </Tabs.Content>
                    </Tabs>
                    <View
                        style={{
                            gap: spaces.spacingSm
                        }}
                    >
                        <Text
                            variant="headlineSmallSize"
                            accessibilityRole="header"
                            color="high"
                        >
                            {t("blocks.events01.faqTitle")}
                        </Text>
                        <Accordion
                            isCollapsible={true}
                            defaultValue="kids"
                            variant="plain"
                        >
                            {FAQ.map(renderFaq)}
                        </Accordion>
                    </View>
                </View>
                <Card
                    style={{
                        width: isWide ? shapeConfigs.cardWidth + spaces.spacingXl : "100%"
                    }}
                    variant="outline"
                >
                    <Card.Header>
                        <Card.Title>
                            {t("blocks.events01.ticketsTitle")}
                        </Card.Title>
                        <Card.Description>
                            {t("blocks.events01.ticketsLead")}
                        </Card.Description>
                    </Card.Header>
                    <Card.Content>
                        <View
                            style={{
                                gap: spaces.spacingMd
                            }}
                        >
                            <RadioGroup
                                accessibilityLabel={t("blocks.events01.ticketsTitle")}
                                onValueChange={handleTicketChange}
                                style={{
                                    gap: spaces.spacingMd
                                }}
                                value={ticket}
                            >
                                {TICKETS.map(renderTicket)}
                            </RadioGroup>
                            <NumericInput
                                hintText={t("blocks.events01.quantityHint", [
                                    MAX_TICKETS
                                ])}
                                title={t("blocks.events01.quantity")}
                                onChangeText={handleQuantityChange}
                                spreadBehaviour="stretch"
                                value={String(count)}
                                isShowStepper={true}
                                max={MAX_TICKETS}
                                min={1}
                            />
                            <Separator/>
                            <View
                                style={{
                                    gap: spaces.spacingSm
                                }}
                            >
                                <View
                                    style={stylesheet.spread}
                                >
                                    <Text
                                        variant="bodyMediumSize"
                                        color="mid"
                                    >
                                        {t("blocks.events01.line", [
                                            t(`blocks.events01.ticket.${selected.key}`),
                                            count
                                        ])}
                                    </Text>
                                    <Text
                                        variant="bodyMediumSize"
                                        color="high"
                                    >
                                        {currency.format(subtotal)}
                                    </Text>
                                </View>
                                <View
                                    style={stylesheet.spread}
                                >
                                    <Text
                                        variant="bodyMediumSize"
                                        color="mid"
                                    >
                                        {t("blocks.events01.fee")}
                                    </Text>
                                    <Text
                                        variant="bodyMediumSize"
                                        color="high"
                                    >
                                        {currency.format(SERVICE_FEE)}
                                    </Text>
                                </View>
                                <View
                                    style={stylesheet.spread}
                                >
                                    <Text
                                        variant="titleMediumSize"
                                        color="high"
                                    >
                                        {t("blocks.events01.total")}
                                    </Text>
                                    <Text
                                        variant="headlineSmallSize"
                                        color="high"
                                    >
                                        {currency.format(total)}
                                    </Text>
                                </View>
                            </View>
                            <Button
                                title={t(isOrdered ? "blocks.events01.ordered" : "blocks.events01.buy")}
                                onPress={handleOrderPress}
                                spreadBehaviour="stretch"
                                isDisabled={isOrdered}
                                variant="filled"
                                type="primary"
                                size="large"
                            />
                            <Alert
                                description={t("blocks.events01.noteBody")}
                                title={t("blocks.events01.noteTitle")}
                                icon={InfoIcon}
                                type="info"
                            />
                        </View>
                    </Card.Content>
                </Card>
            </View>
        </View>
        <Separator/>
        <View
            style={[
                stylesheet.spread,
                isCompact ? stylesheet.footerCompact : stylesheet.wrap,
                stylesheet.shell,
                {
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.events01.copyright")}
            </Text>
            <View
                style={[
                    stylesheet.row,
                    stylesheet.wrap,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                {FOOTER_LINKS.map(renderFooterLink)}
            </View>
        </View>
    </ScrollView>;
};
export default Events01;
